// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-14 — Notification delivery.
 *
 * Production, 2026-08-21: case #1015 had FOUR outbound emails sitting in state 'failed'
 * (support_activity + support_requester, at 19:18 and again at 22:19). Nothing in the product
 * said so. The support team believed the customer had been told; the customer had not been. It
 * surfaced only because a read-only diagnostic was run by hand.
 *
 * That is the invite defect one rail over: "we queued a row" being reported as "the person was
 * notified". This page is the screen that disagrees when they diverge.
 *
 * Failures first, by default. The delivered rows are noise when you are looking for the ones that
 * did not arrive — "show everything" is the opt-in, not the default.
 *
 * NO subject and NO body are displayed, deliberately. Support mail subjects are
 * "[Support #N] {ticket subject}" and ticket subjects are customer-authored free text that may
 * carry PHI; the diagnostic that found this incident labels its own output "no subject - PHI".
 * The failure REASON is shown, because a row that says "failed" without one is the same dead end.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MailWarning, RefreshCw } from 'lucide-react'
import * as api from '../../api/client'
import BackToSupportTools from './BackToSupportTools'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState, EmptyState, relativeTime } from '../../components/workflow/StatePills'
import DeliveryPill from '../../components/workflow/DeliveryPill'
import SearchableSelect from '../../components/common/SearchableSelect'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: what each column shows, for its header funnel. Keys are the lower-cased header labels.
const DELIVERY_FUNNELS: FunnelColumn<api.SupportNotificationDelivery>[] = [
  { key: 'case',         label: 'Case',         value: r => r.ticket_no != null ? `#${r.ticket_no}` : 'unknown case' },
  { key: 'recipient',    label: 'Recipient',    value: r => r.recipient },
  { key: 'notification', label: 'Notification', value: r => KIND_LABELS[r.kind] ?? r.kind },
  { key: 'delivery',     label: 'Delivery',     value: r => r.status ?? 'never queued' },
  { key: 'when',         label: 'When',         value: r => r.created_at },
]

export default function SupportNotificationDeliveryPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<api.SupportNotificationDelivery[]>([])
  const [failedCount, setFailedCount] = useState(0)
  const [showAll, setShowAll] = useState(false)
  const [days, setDays] = useState(14)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    setLoading(true)
    api.listSupportNotificationDelivery({ all: showAll, days })
      .then(r => { setRows(r.items); setFailedCount(r.failed_count); setError(null) })
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }
  useEffect(load, [showAll, days])

  // The header funnels filter the whole loaded set.
  const fx = useTableFunnels(rows, DELIVERY_FUNNELS)

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <BackToSupportTools />
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MailWarning size={21} /> Notification delivery
        </h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
          Whether case notifications actually reached anyone. Queued is not delivered — this is the
          screen that says so when they differ.
        </div>
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
          Show delivered too
        </label>
        {/* Gate 19: the shared SearchableSelect, never a raw HTML dropdown. CI rejected the first cut. */}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          Window
          <span style={{ minWidth: 130, display: 'inline-block' }}>
            <SearchableSelect value={String(days)} onChange={v => setDays(Number(v))} ariaLabel="Delivery window"
              options={[{ value: '7', label: '7 days' }, { value: '14', label: '14 days' },
                        { value: '30', label: '30 days' }, { value: '90', label: '90 days' }]}
              style={{ fontSize: 13 }} />
          </span>
        </label>
        <button onClick={load} disabled={loading}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--color-border)', borderRadius: 8, padding: '6px 11px', fontSize: 12.5, background: 'var(--color-bg)', color: 'var(--color-text)', cursor: loading ? 'default' : 'pointer' }}>
          <RefreshCw size={13} /> Refresh
        </button>
        {!loading && (
          <span style={{ fontSize: 12.5, color: failedCount > 0 ? '#b91c1c' : 'var(--color-text-mute)', fontWeight: failedCount > 0 ? 700 : 400 }}>
            {failedCount > 0 ? `${failedCount} notification${failedCount === 1 ? '' : 's'} did not arrive` : 'No failed deliveries in this window'}
          </span>
        )}
      </div>

      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 14 }}>
        {loading ? <LoadingState label="Loading delivery…" /> :
         rows.length === 0 ? (
          <EmptyState message={showAll
            ? 'No case notifications in this window.'
            : 'No failed deliveries in this window — every case notification reached its recipient.'} />
         ) : (
          <>
          {fx.chips}
          <div style={{ overflowX: 'auto' }}>
            <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{['Case', 'Recipient', 'Notification', 'Delivery', 'When'].map(h => <th key={h} style={th}>{h} {fx.funnel(h.toLowerCase())}</th>)}</tr></thead>
              <tbody>
                {fx.rows.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={td}>
                      {r.ticket_no != null ? (
                        <button onClick={() => navigate(`/admin/support/${r.ticket_no}`)}
                          style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--color-primary,#2563eb)', cursor: 'pointer', fontWeight: 600 }}>
                          #{r.ticket_no}
                        </button>
                      ) : (
                        // An orphaned notification is itself a finding, so it is shown rather than filtered out.
                        <span style={{ color: 'var(--color-text-mute)' }} title="No matching case — the ticket may have been removed">unknown case</span>
                      )}
                      {r.pharmacy_name && <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>{r.pharmacy_name}</div>}
                    </td>
                    <td style={td}>{r.recipient}</td>
                    <td style={td}><span style={kindChip}>{KIND_LABELS[r.kind] ?? r.kind}</span></td>
                    <td style={td}>
                      <DeliveryPill status={r.status} sentAt={r.sent_at} error={r.last_error} attempts={r.attempts} />
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{relativeTime(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
         )}
      </div>
    </div>
  )
}

// Raw kinds are internal names. Support staff should not have to know that support_requester means
// "the customer was told about a reply".
const KIND_LABELS: Record<string, string> = {
  support_activity: 'Case activity (support)',
  support_requester: 'Case reply (customer)',
  support_mention: 'Mention',
  support_sla_breach: 'SLA breach',
  support_team_invite: 'Team invite',
  support_team_added: 'Added to team',
  invitation: 'Invitation',
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 8px', color: 'var(--color-text-mute)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px' }
const td: React.CSSProperties = { padding: '9px 8px', verticalAlign: 'top' }
const kindChip: React.CSSProperties = { fontSize: 12, padding: '2px 8px', borderRadius: 999, background: 'var(--color-bg)', border: '1px solid var(--color-border)', color: 'var(--color-text-mute)' }
